import { useState, useCallback, useMemo, useEffect } from 'react';
import { useDebounce } from '@/shared/hooks/use-debounce';
import type {
  HistoryFilters,
  HistorySortBy,
  HistoryItem,
} from '../model/history.types';
import { useHistorySearchStore } from '../model/history-search-store';

export type FilterType = 'all' | 'favorites' | 'project';
export type SubtypeFilter = 'all' | 'render' | 'album' | 'estimate';

interface UseHistoryFiltersProps {
  initialFilters?: HistoryFilters;
  initialSort?: HistorySortBy;
  initialSubtype?: SubtypeFilter;
  concepts?: HistoryItem[];
}

interface UseHistoryFiltersReturn {
  // Filter values
  filterType: FilterType;
  subtype: SubtypeFilter;
  searchQuery: string;
  selectedProjectId: string;
  sortBy: HistorySortBy;

  // Debounced values
  debouncedSearchQuery: string;
  activeFilters: HistoryFilters;

  // Setters
  setFilterType: (type: FilterType) => void;
  setSubtype: (subtype: SubtypeFilter) => void;
  setSearchQuery: (query: string) => void;
  setSelectedProjectId: (id: string) => void;
  setSortBy: (sort: HistorySortBy) => void;

  // Utilities
  clearFilters: () => void;
  hasActiveFilters: boolean;
  availableProjects: Array<{ id: string; name: string }>;
}

/**
 * Hook for managing history filters
 */
export function useHistoryFilters({
  initialFilters = {},
  initialSort = 'date-desc',
  initialSubtype = 'all',
  concepts = [],
}: UseHistoryFiltersProps = {}): UseHistoryFiltersReturn {
  // Use global store for search query (shared with header)
  const searchQuery = useHistorySearchStore((state) => state.searchQuery);
  const setSearchQueryStore = useHistorySearchStore(
    (state) => state.setSearchQuery
  );
  const clearSearch = useHistorySearchStore((state) => state.clearSearch);

  // Local state for other filters
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [subtype, setSubtype] = useState<SubtypeFilter>(initialSubtype);
  const [selectedProjectId, setSelectedProjectId] = useState(
    initialFilters.project_id || ''
  );
  const [sortBy, setSortBy] = useState<HistorySortBy>(initialSort);

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery, 600);

  // Update filter type based on initial filters
  useEffect(() => {
    if (initialFilters.is_favorite) {
      setFilterType('favorites');
    } else if (initialFilters.project_id) {
      setFilterType('project');
      setSelectedProjectId(initialFilters.project_id);
    }
  }, [initialFilters]);

  // Compute active filters
  const activeFilters = useMemo<HistoryFilters>(() => {
    const filters: HistoryFilters = {};

    if (filterType === 'favorites') {
      filters.is_favorite = true;
    } else if (filterType === 'project' && selectedProjectId) {
      filters.project_id = selectedProjectId;
    }

    if (debouncedSearchQuery.trim()) {
      filters.search = debouncedSearchQuery.trim();
    }

    if (subtype !== 'all') {
      filters.subtype = subtype;
    }

    return filters;
  }, [filterType, selectedProjectId, debouncedSearchQuery, subtype]);

  // Extract unique projects from concepts
  const availableProjects = useMemo(() => {
    const projectMap = new Map<string, { id: string; name: string }>();

    concepts.forEach((concept) => {
      const projectId = concept.relationships.project.data.id;
      const projectName = concept.relationships.project.meta.name;

      if (projectId && projectName && !projectMap.has(projectId)) {
        projectMap.set(projectId, { id: projectId, name: projectName });
      }
    });

    return Array.from(projectMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [concepts]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilterType('all');
    setSubtype('all');
    clearSearch();
    setSelectedProjectId('');
    setSortBy('date-desc');
  }, [clearSearch]);

  // Check if any filters are active
  // `subtype` is a tab-like dimension (renders / albums / both), not a filter — its empty
  // state should be the generic "renders and albums will appear here", not "no results".
  const hasActiveFilters = useMemo(() => {
    return (
      filterType !== 'all' ||
      searchQuery.trim() !== '' ||
      sortBy !== 'date-desc'
    );
  }, [filterType, searchQuery, sortBy]);

  // Handle filter type change
  const handleFilterTypeChange = useCallback((type: FilterType) => {
    setFilterType(type);
    // Reset project selection when switching away from project filter
    if (type !== 'project') {
      setSelectedProjectId('');
    }
  }, []);

  return {
    // Filter values
    filterType,
    subtype,
    searchQuery,
    selectedProjectId,
    sortBy,

    // Debounced values
    debouncedSearchQuery,
    activeFilters,

    // Setters
    setFilterType: handleFilterTypeChange,
    setSubtype,
    setSearchQuery: setSearchQueryStore,
    setSelectedProjectId,
    setSortBy,

    // Utilities
    clearFilters,
    hasActiveFilters,
    availableProjects,
  };
}
